/**
 * All-synthesized Web Audio, rebuilt as the layered generative system
 * (LOCKED-DESIGN 18.4 / 2.2). Zero assets — synth IS the aesthetic.
 *
 * Buses:
 *   music  : melodic layers -> lowpass -> duck -> volume; percussion joins
 *            after the filter (hats through a 620 Hz lowpass would vanish).
 *   sfx    : one-shot vocabulary voices, host sfxVolume.
 *   alarm  : PRIORITY bus — critical-overflow klaxon + red-orb fuse ticks.
 *            Never ducked; instead it sidechain-ducks the music bus (2.2:
 *            alarms and fuse cues always take priority over music).
 *   master : everything -> DynamicsCompressor limiter -> destination.
 *
 * Arrangement comes from the pure MusicDirector (three-axis model: phase ->
 * layers, chain -> percussion, overflow -> filter tension); this class only
 * renders notes and enforces mix safety (voice caps, ducking, limiter).
 *
 * Respects host mute/volume via poll() each frame (v1 context has no change
 * events). AudioContext is created on start(), which runs after the PLAY
 * click, so autoplay policy is satisfied. All randomness here is cosmetic
 * (noise buffers, sample offsets) and never touches the gameplay stream.
 */

import type { BlastLocation, ChainBreakCause, GradeBandLetter, OverflowWarningLevel, SoundEvents } from "./SoundEvents.js";
import { arrangementForPhase, MusicDirector, musicFilterCutoff, type MusicNote } from "./MusicDirector.js";
import { PriorityDucker, VoiceCap } from "./mix.js";

const MASTER_GAIN = 0.9;
const MUSIC_BED = 0.35;
/** Scheduler lookahead — well past a dropped frame, well under a bar. */
const LOOKAHEAD = 0.35;
/** Global polyphony cap (music + sfx one-shots), steal-oldest. */
const MAX_VOICES = 40;
/** Hard cap for the rapid incinerate ding ladder (volley policy). */
const MAX_DING_VOICES = 6;
/** Rapid dings inside this window climb the volley pitch ladder. */
const VOLLEY_WINDOW = 0.3;

/** Three-axis music model input (18.4): one knob per axis, no max() bundling. */
export interface AudioAxes {
  /** Arrangement phase 1..6. */
  phase: number;
  /** Chain tier 0..4 (x1..x5) — percussion density. */
  chainTier: number;
  /** Facility pressure 0..1 — filter tension only; thresholds fire events. */
  overflow01: number;
}

interface VoiceHandle {
  gain: GainNode;
  stop: (when: number) => void;
}

export class Sound implements SoundEvents {
  private ac: AudioContext | null = null;
  private limiter!: DynamicsCompressorNode;
  private master!: GainNode;
  private musicVol!: GainNode; // host music volume × bed level
  private musicDuck!: GainNode; // priority sidechain target
  private musicFilter!: BiquadFilterNode;
  private percBus!: GainNode; // ducked with music, bypasses the lowpass
  private sfxBus!: GainNode;
  private alarmBus!: GainNode; // priority bus — never ducked
  private padGain!: GainNode;
  private padFifthGain!: GainNode;
  private noiseBuf!: AudioBuffer;

  private director: MusicDirector | null = null;
  private readonly ducker = new PriorityDucker();
  private readonly voices = new VoiceCap<VoiceHandle>(MAX_VOICES);
  private readonly dings = new VoiceCap<VoiceHandle>(MAX_DING_VOICES);
  private lastDuck = 1;
  private volleyStep = 0;
  private lastDingAt = -1;

  /** critical-overflow klaxon loop nodes */
  private alarmOsc: OscillatorNode | null = null;
  private alarmGain: GainNode | null = null;
  private overloadOsc: OscillatorNode | null = null;
  private overloadGain: GainNode | null = null;

  start(): void {
    if (this.ac) {
      void this.ac.resume();
      return;
    }
    const ac = new AudioContext();
    this.ac = ac;

    // master -> limiter -> destination (mix safety: nothing clips the DAC)
    this.limiter = ac.createDynamicsCompressor();
    this.limiter.threshold.value = -10;
    this.limiter.knee.value = 4;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.25;
    this.limiter.connect(ac.destination);
    this.master = ac.createGain();
    this.master.gain.value = MASTER_GAIN;
    this.master.connect(this.limiter);

    this.sfxBus = ac.createGain();
    this.sfxBus.connect(this.master);
    this.alarmBus = ac.createGain();
    this.alarmBus.connect(this.master);

    this.musicVol = ac.createGain();
    this.musicVol.gain.value = MUSIC_BED;
    this.musicVol.connect(this.master);
    this.musicDuck = ac.createGain();
    this.musicDuck.gain.value = 1;
    this.musicDuck.connect(this.musicVol);
    this.musicFilter = ac.createBiquadFilter();
    this.musicFilter.type = "lowpass";
    this.musicFilter.frequency.value = musicFilterCutoff(1, 0);
    this.musicFilter.connect(this.musicDuck);
    this.percBus = ac.createGain();
    this.percBus.connect(this.musicDuck);

    // shared noise buffer for whooshes/crunches/percussion (cosmetic randomness)
    const len = ac.sampleRate;
    this.noiseBuf = ac.createBuffer(1, len, ac.sampleRate);
    const data = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;

    // pad layer: two detuned saws forever (the phase-1 identity), plus a
    // fifth that phase 5+ fades in for harmonic density
    this.padGain = ac.createGain();
    this.padGain.gain.value = arrangementForPhase(1).padLevel;
    this.padGain.connect(this.musicFilter);
    for (const detune of [0, 7]) {
      const osc = ac.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = 55;
      osc.detune.value = detune;
      osc.connect(this.padGain);
      osc.start();
    }
    this.padFifthGain = ac.createGain();
    this.padFifthGain.gain.value = 0.0001;
    this.padFifthGain.connect(this.musicFilter);
    const fifth = ac.createOscillator();
    fifth.type = "sawtooth";
    fifth.frequency.value = 82.41; // E2
    fifth.detune.value = 4;
    fifth.connect(this.padFifthGain);
    fifth.start();

    this.director = new MusicDirector(ac.currentTime + 0.05);
  }

  suspend(): void {
    void this.ac?.suspend();
  }
  resume(): void {
    void this.ac?.resume();
  }

  /** Per-frame: host volume poll + three-axis music scheduling + duck follow. */
  poll(muted: boolean, masterVolume: number, sfxVolume: number, musicVolume: number, axes: AudioAxes): void {
    const ac = this.ac;
    const director = this.director;
    if (!ac || !director) return;
    const now = ac.currentTime;
    this.master.gain.value = muted ? 0 : MASTER_GAIN * masterVolume;
    this.sfxBus.gain.value = sfxVolume;
    this.alarmBus.gain.value = sfxVolume; // priority = ducking, not volume override
    this.musicVol.gain.value = MUSIC_BED * musicVolume;
    this.voices.prune(now);
    this.dings.prune(now);

    director.setAxes(axes.phase, axes.chainTier);
    director.schedule(now, now + LOOKAHEAD, (note) => this.renderNote(note));

    // bar-quantized arrangement follow: pad width/level + filter brightness
    const arr = arrangementForPhase(director.appliedPhase);
    this.musicFilter.frequency.setTargetAtTime(musicFilterCutoff(director.appliedPhase, axes.overflow01), now, 0.25);
    this.padGain.gain.setTargetAtTime(arr.padLevel, now, 0.4);
    this.padFifthGain.gain.setTargetAtTime(arr.padFifth ? 0.03 : 0.0001, now, 0.4);

    // sidechain duck: fast attack under priority cues, slow musical release
    const duck = this.ducker.duckTarget(now);
    if (duck !== this.lastDuck) {
      this.musicDuck.gain.setTargetAtTime(duck, now, duck < this.lastDuck ? 0.04 : 0.5);
      this.lastDuck = duck;
    }
  }

  // ---------------- music note rendering ----------------

  private renderNote(note: MusicNote): void {
    switch (note.voice) {
      case "bass": {
        // the phase-1 bass keeps the old loop's square identity
        this.toneAt("square", note.freq, note.freq, note.dur, note.gain, note.time, this.musicFilter);
        break;
      }
      case "arp": {
        this.pluck(note.freq, note.dur, note.gain, note.time);
        break;
      }
      case "lead": {
        this.leadNote(note.freq, note.dur, note.gain, note.time);
        break;
      }
      case "hat": {
        this.noiseAt("highpass", 7000, 7000, note.dur, note.gain, note.time, this.percBus, 0.7);
        break;
      }
      case "kick": {
        this.toneAt("sine", note.freq, 44, note.dur, note.gain, note.time, this.percBus);
        break;
      }
      case "snare": {
        this.noiseAt("bandpass", note.freq, note.freq, note.dur, note.gain, note.time, this.percBus, 0.9);
        this.toneAt("triangle", 190, 130, 0.06, note.gain * 0.5, note.time, this.percBus);
        break;
      }
      case "pad":
        break; // continuous layer, handled by padGain
      default:
        break;
    }
  }

  /** Fast-decay triangle pluck for the arp layer. */
  private pluck(freq: number, dur: number, gain: number, at: number): void {
    const ac = this.ac!;
    const osc = ac.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g);
    g.connect(this.musicFilter);
    this.spawn(g, osc, at, dur);
  }

  /** Wide detuned saw pair for the lead layer. */
  private leadNote(freq: number, dur: number, gain: number, at: number): void {
    const ac = this.ac!;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    g.connect(this.musicFilter);
    const oscs: OscillatorNode[] = [];
    for (const detune of [-5, 5]) {
      const osc = ac.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = freq;
      osc.detune.value = detune;
      osc.connect(g);
      oscs.push(osc);
    }
    const stopAt = at + dur + 0.05;
    for (const osc of oscs) {
      osc.start(at);
      osc.stop(stopAt);
    }
    this.register({ gain: g, stop: (when) => { for (const osc of oscs) osc.stop(when); } }, stopAt);
  }

  // ---------------- voice management ----------------

  private register(handle: VoiceHandle, ends: number, cap: VoiceCap<VoiceHandle> = this.voices): void {
    const stolen = cap.register(handle, ends);
    if (stolen) this.kill(stolen);
  }

  /** Steal-oldest: fast fade so the cut is inaudible, then stop. */
  private kill(handle: VoiceHandle): void {
    const ac = this.ac!;
    const t = ac.currentTime;
    handle.gain.gain.cancelScheduledValues(t);
    handle.gain.gain.setTargetAtTime(0.0001, t, 0.015);
    try {
      handle.stop(t + 0.06);
    } catch {
      // already stopped
    }
  }

  private spawn(g: GainNode, osc: OscillatorNode, at: number, dur: number, cap: VoiceCap<VoiceHandle> = this.voices): void {
    osc.start(at);
    osc.stop(at + dur + 0.05);
    this.register({ gain: g, stop: (when) => osc.stop(when) }, at + dur + 0.05, cap);
  }

  /** One-shot swept oscillator at an ABSOLUTE AudioContext time. */
  private toneAt(
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    gain: number,
    t: number,
    bus?: AudioNode,
    cap?: VoiceCap<VoiceHandle>,
  ): void {
    const ac = this.ac;
    if (!ac) return;
    const osc = ac.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(bus ?? this.sfxBus);
    this.spawn(g, osc, t, dur, cap);
  }

  /** One-shot swept oscillator; `when` is an offset in seconds from now. */
  private tone(
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    gain: number,
    when = 0,
    bus?: AudioNode,
    cap?: VoiceCap<VoiceHandle>,
  ): void {
    const ac = this.ac;
    if (!ac) return;
    this.toneAt(type, f0, f1, dur, gain, ac.currentTime + when, bus, cap);
  }

  /** One-shot filtered-noise voice at an ABSOLUTE AudioContext time. */
  private noiseAt(
    filterType: BiquadFilterType,
    f0: number,
    f1: number,
    dur: number,
    gain: number,
    t: number,
    bus?: AudioNode,
    q = 1.2,
  ): void {
    const ac = this.ac;
    if (!ac) return;
    const src = ac.createBufferSource();
    src.buffer = this.noiseBuf;
    const filter = ac.createBiquadFilter();
    filter.type = filterType;
    filter.Q.value = q;
    filter.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) filter.frequency.exponentialRampToValueAtTime(Math.max(40, f1), t + dur);
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(bus ?? this.sfxBus);
    src.start(t, Math.random() * 0.5, dur + 0.05);
    this.register({ gain: g, stop: (when) => src.stop(when) }, t + dur + 0.05);
  }

  /** One-shot filtered-noise voice; `when` is an offset in seconds from now. */
  private noise(
    filterType: BiquadFilterType,
    f0: number,
    f1: number,
    dur: number,
    gain: number,
    when = 0,
    bus?: AudioNode,
    q = 1.2,
  ): void {
    const ac = this.ac;
    if (!ac) return;
    this.noiseAt(filterType, f0, f1, dur, gain, ac.currentTime + when, bus, q);
  }

  private whoosh(f0: number, f1: number, dur: number, gain: number, type: BiquadFilterType = "bandpass", when = 0): void {
    this.noise(type, f0, f1, dur, gain, when);
  }

  // ---------------- traversal + movement SFX (pitch-mapped identity kept) ----------------

  jump(): void {
    this.tone("square", 240, 480, 0.13, 0.16);
  }

  dash(): void {
    this.whoosh(900, 180, 0.2, 0.3);
  }

  grab(count = 1): void {
    const k = 1 + (count - 1) * 0.12; // each capture rings higher
    this.tone("sine", 280 * k, 540 * k, 0.14, 0.18);
  }

  throwSfx(count = 1): void {
    const g = Math.min(0.45, 0.24 + count * 0.04); // volleys hit harder
    this.whoosh(1400, 400, 0.14 + count * 0.01, g, "highpass");
    this.tone("sawtooth", 190, 90, 0.12, 0.08 + count * 0.015);
  }

  portalZap(): void {
    this.tone("sine", 500, 1300, 0.22, 0.2);
    this.tone("triangle", 750, 1950, 0.22, 0.12, 0.03);
  }

  land(): void {
    this.whoosh(320, 90, 0.1, 0.14, "lowpass");
  }

  /**
   * Small per-piece incineration tick — the payoff is cleanLoad(). Volley
   * policy (locked): rapid dings arpeggiate up a pitch ladder under a hard
   * dedicated voice cap, so a 6-piece volley reads as one rising gesture.
   */
  incinerate(combo: number): void {
    const ac = this.ac;
    if (!ac) return;
    const now = ac.currentTime;
    this.volleyStep = now - this.lastDingAt < VOLLEY_WINDOW ? Math.min(this.volleyStep + 1, 10) : 0;
    this.lastDingAt = now;
    this.whoosh(700, 120, 0.16, 0.16, "lowpass");
    const ladder = Math.pow(2, this.volleyStep / 6); // +2 semitones per rung
    const f = Math.min(4200, 620 * Math.pow(1.06, Math.min(combo, 12)) * ladder);
    this.tone("triangle", f, f * 1.01, 0.16, 0.13, 0.02, this.sfxBus, this.dings);
  }

  // ---------------- SoundEvents vocabulary ----------------

  cleanLoad(size: number, chainTier: number): void {
    // THE reward beat: ascending payoff arpeggio — root climbs with chain,
    // note count with load size, stacked fourths so it's bright, not saccharine
    const root = 440 * Math.pow(1.12, Math.max(0, chainTier - 1));
    const notes = Math.min(5, 2 + Math.floor(size / 3));
    for (let i = 0; i < notes; i++) {
      const f = root * Math.pow(1.335, i);
      this.tone("triangle", f, f * 1.01, 0.22, 0.24, i * 0.07);
    }
    if (chainTier >= 4) this.tone("sine", root * 4, root * 4, 0.3, 0.1, notes * 0.07); // top-tier sparkle
    this.whoosh(500, 2400, 0.28, 0.16, "highpass");
  }

  partialLoad(): void {
    // truncated payoff: the arpeggio starts, then stops short
    this.tone("triangle", 440, 440, 0.14, 0.15);
    this.tone("triangle", 392, 392, 0.18, 0.12, 0.11);
  }

  missLoad(): void {
    // dull thud — soft-bodied, unlike the deny buzzer
    this.tone("sine", 110, 48, 0.28, 0.22);
    this.whoosh(260, 60, 0.22, 0.22, "lowpass");
  }

  chainUp(tier: number): void {
    const f = 660 * Math.pow(1.12, tier); // tier-pitched stinger
    this.tone("square", f, f, 0.09, 0.16);
    this.tone("square", f * 1.5, f * 1.5, 0.14, 0.16, 0.07);
  }

  chainBreak(cause: ChainBreakCause): void {
    // detune drop: a beating saw pair slides apart and falls
    const drop = cause === "expiry" ? 0.55 : 0.42; // harder causes fall further
    this.tone("sawtooth", 494, 494 * drop, 0.3, 0.15);
    this.tone("sawtooth", 500, 470 * drop, 0.34, 0.13);
  }

  gradeBand(direction: "up" | "down", band: GradeBandLetter): void {
    const roots: Record<GradeBandLetter, number> = { C: 262, B: 330, A: 392, S: 523 };
    const root = roots[band];
    if (direction === "up") {
      this.tone("sine", root, root, 0.4, 0.16);
      this.tone("sine", root * 1.5, root * 1.5, 0.5, 0.14, 0.12);
    } else {
      this.tone("sine", root * 1.5, root * 1.5, 0.35, 0.14);
      this.tone("sine", root, root, 0.5, 0.14, 0.12);
    }
  }

  ticketDone(): void {
    for (const [i, f] of [523, 659, 880].entries()) this.tone("triangle", f, f, 0.2, 0.2, i * 0.08);
  }

  purge(): void {
    // pneumatic slam: air release, then the chute door lands + latch clank
    this.whoosh(2400, 300, 0.28, 0.24, "highpass");
    this.tone("sine", 140, 50, 0.2, 0.28, 0.1);
    this.whoosh(400, 80, 0.15, 0.28, "lowpass", 0.1);
    this.tone("square", 190, 180, 0.04, 0.1, 0.13);
  }

  deny(): void {
    // clipped industrial buzz: square carrier with hard square AM, cut short
    const ac = this.ac;
    if (!ac) return;
    const t = ac.currentTime;
    const osc = ac.createOscillator();
    osc.type = "square";
    osc.frequency.value = 115;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.14, t);
    g.gain.setValueAtTime(0.0001, t + 0.09); // hard clip, by design
    const lfo = ac.createOscillator();
    lfo.type = "square";
    lfo.frequency.value = 30;
    const lfoGain = ac.createGain();
    lfoGain.gain.value = 0.09;
    lfo.connect(lfoGain);
    lfoGain.connect(g.gain);
    osc.connect(g);
    g.connect(this.sfxBus);
    lfo.start(t);
    lfo.stop(t + 0.1);
    this.spawn(g, osc, t, 0.06);
  }

  death(): void {
    // crunch, then the respawn shimmer rises out of the wreckage
    this.whoosh(1800, 90, 0.35, 0.34, "lowpass");
    this.tone("sawtooth", 260, 48, 0.4, 0.22, 0.03);
    for (const [i, f] of [523, 659, 784, 1046].entries()) {
      this.tone("triangle", f, f * 1.01, 0.18, 0.07 + i * 0.01, 0.42 + i * 0.09);
    }
  }

  blast(location: BlastLocation): void {
    // a REAL explosion — noise burst + sub thump. NOT the overflow alarm
    // voice and never shared with it (LOCKED vocabulary split).
    if (location === "arena") {
      this.noise("lowpass", 2600, 50, 0.6, 0.55);
      this.tone("sine", 70, 26, 0.6, 0.5);
      this.noise("bandpass", 3000, 900, 0.12, 0.2, 0.03); // debris crackle
      this.noise("bandpass", 2400, 700, 0.1, 0.14, 0.11);
    } else {
      // contained: muffled thump + reinforced-glass rattle, no open-air boom
      this.noise("lowpass", 500, 60, 0.45, 0.34);
      this.tone("sine", 64, 30, 0.45, 0.3);
      this.noise("bandpass", 2800, 2000, 0.3, 0.13, 0.04);
    }
  }

  fuseTick(secondsLeft: number): void {
    // priority metronome: called once per remaining second; the tick itself
    // subdivides as the fuse shortens so urgency escalates inside the second
    const ac = this.ac;
    if (!ac) return;
    const subdivisions = secondsLeft > 5 ? 1 : secondsLeft > 3 ? 2 : 4;
    const f = 700 + Math.max(0, 8 - secondsLeft) * 110;
    for (let i = 0; i < subdivisions; i++) {
      this.tone("square", f, f, 0.045, 0.18, i / subdivisions, this.alarmBus);
    }
    this.ducker.fusePulse(ac.currentTime); // sidechain: music pumps under the fuse
  }

  alarmStart(): void {
    const ac = this.ac;
    if (!ac || this.alarmOsc) return;
    this.ducker.setAlarm(true);
    // two-tone industrial klaxon: saw swept by a square LFO, lowpassed so it
    // reads urgent-not-shrill (facility voice, not horror)
    const osc = ac.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = 440;
    const lfo = ac.createOscillator();
    lfo.type = "square";
    lfo.frequency.value = 2.2;
    const lfoGain = ac.createGain();
    lfoGain.gain.value = 120;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    const soften = ac.createBiquadFilter();
    soften.type = "lowpass";
    soften.frequency.value = 1300;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.13, ac.currentTime + 0.1);
    osc.connect(soften);
    soften.connect(g);
    g.connect(this.alarmBus);
    osc.start();
    lfo.start();
    this.alarmOsc = osc;
    this.alarmGain = g;
    osc.onended = () => lfo.stop();
  }

  alarmEnd(): void {
    this.ducker.setAlarm(false);
    const ac = this.ac;
    if (!ac || !this.alarmOsc || !this.alarmGain) return;
    this.alarmGain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.15);
    this.alarmOsc.stop(ac.currentTime + 0.2);
    this.alarmOsc = null;
    this.alarmGain = null;
  }

  overflowWarning(level: OverflowWarningLevel): void {
    // threshold escalation on the priority bus. NOT the blast voice.
    if (level === "warning") {
      this.tone("square", 440, 440, 0.09, 0.18, 0, this.alarmBus);
      this.tone("square", 440, 440, 0.09, 0.18, 0.16, this.alarmBus);
    } else {
      for (let i = 0; i < 3; i++) this.tone("square", 554, 554, 0.08, 0.2, i * 0.11, this.alarmBus);
    }
    if (this.ac) this.ducker.fusePulse(this.ac.currentTime, 0.5); // brief duck so the beeps read
  }

  shutterWarn(): void {
    for (let i = 0; i < 3; i++) this.tone("square", 330, 300, 0.09, 0.16, i * 0.14);
    this.whoosh(1200, 300, 0.2, 0.12);
  }

  conveyorWarn(): void {
    // deadpan facility "vehicle reversing" alarm: spaced paired beeps over a
    // low belt-motor sweep. Distinct from the shutter voice by design.
    for (let i = 0; i < 3; i++) this.tone("square", 740, 740, 0.08, 0.13, i * 0.22);
    this.whoosh(240, 90, 0.55, 0.12, "lowpass");
  }

  panelExpireWarn(): void {
    // powering-down motif: three descending steps — a route panel rotates out
    this.tone("triangle", 622, 622, 0.11, 0.16);
    this.tone("triangle", 466, 466, 0.11, 0.14, 0.12);
    this.tone("triangle", 311, 311, 0.16, 0.14, 0.24);
  }

  batchTelegraph(): void {
    this.tone("sine", 520, 700, 0.1, 0.14);
    this.tone("sine", 700, 940, 0.12, 0.12, 0.09);
  }

  baffleTurn(blocked: boolean): void {
    // mechanical detent: closing thunks low, opening releases high
    if (blocked) this.tone("square", 160, 90, 0.11, 0.14);
    else this.tone("square", 240, 340, 0.11, 0.12);
  }

  // ---- feed-the-maw recovery staging (LOCKED-DESIGN 18.12) ----

  padSpinUp(): void {
    // return-pad spin-up: a rising magnetic whine as the launch charges
    this.tone("sine", 260, 620, 0.5, 0.08, 0.02);
    this.tone("triangle", 130, 260, 0.5, 0.06);
  }

  chamberHeatWarn(): void {
    // dwell 2s warning: an urgent tick on the priority bus — never a shake
    // (readability while escaping). Brief music duck so the beep reads.
    if (this.ac) this.ducker.fusePulse(this.ac.currentTime, 0.5);
    this.tone("square", 880, 900, 0.05, 0.16);
  }

  chamberCool(): void {
    // chamber exit: a soft pneumatic cool-down hiss as the heat resets
    this.noise("lowpass", 700, 220, 0.3, 0.12, 0.03);
  }

  overloadKlaxon(on: boolean): void {
    // furnace-overload final-5s klaxon (18.12): distinct lower two-tone from the
    // overflow alarm, on the same priority bus (ducks music). The Engine gates it
    // so it never fires over the critical-overflow alarm (overflow owns priority).
    const ac = this.ac;
    if (!ac) return;
    if (on) {
      if (this.overloadOsc) return;
      this.ducker.setAlarm(true);
      const osc = ac.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = 300;
      const lfo = ac.createOscillator();
      lfo.type = "square";
      lfo.frequency.value = 1.6;
      const lfoGain = ac.createGain();
      lfoGain.gain.value = 80;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      const soften = ac.createBiquadFilter();
      soften.type = "lowpass";
      soften.frequency.value = 1000;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.12, ac.currentTime + 0.1);
      osc.connect(soften);
      soften.connect(g);
      g.connect(this.alarmBus);
      osc.start();
      lfo.start();
      osc.onended = () => lfo.stop();
      this.overloadOsc = osc;
      this.overloadGain = g;
    } else {
      this.ducker.setAlarm(false);
      if (!this.overloadOsc || !this.overloadGain) return;
      this.overloadGain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.15);
      this.overloadOsc.stop(ac.currentTime + 0.2);
      this.overloadOsc = null;
      this.overloadGain = null;
    }
  }

  returnEject(bulk: number): void {
    // return-chute pop (conveyance voice): a reject re-enters the arena;
    // brightens slightly with the piece's bulk so a volley reads as a burst
    const f = 340 + Math.min(6, bulk) * 24;
    this.whoosh(500, 1400, 0.14, 0.14, "highpass");
    this.tone("triangle", f, f * 1.3, 0.12, 0.16, 0.02);
  }

  /** happy two-note step chime (tutorial advances) */
  chime(): void {
    this.tone("triangle", 660, 660, 0.12, 0.2);
    this.tone("triangle", 880, 880, 0.16, 0.2, 0.09);
  }

  /**
   * Reinforced-glass bulkhead hit (LOCKED-DESIGN 18.6 enforced glass beat): a
   * dense, hard clank that repels — NOT a shatter. Distinct from the deny
   * buzzer and the red-blast voice so the "sealed, use the transfer system"
   * lesson reads as its own beat.
   */
  glassHit(): void {
    this.tone("square", 300, 118, 0.09, 0.16);          // dense repelling thud
    this.noise("bandpass", 3400, 1200, 0.11, 0.18, 0.01); // glassy rattle
    this.tone("triangle", 1500, 940, 0.15, 0.09, 0.02);   // ringing overtone
  }

  runEnd(win: boolean): void {
    const seq = win ? [523, 659, 784] : [392, 311, 233];
    seq.forEach((f, i) => this.tone("triangle", f, f, 0.25, 0.22, i * 0.14));
  }
}
